import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserControls = ({ users = [], setUsers }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'suspend', 'activate', 'details'

  // Filter users based on search, status, and type
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    const matchesType = userTypeFilter === 'all' || user?.userType === userTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleUserAction = (user, action) => {
    setSelectedUser(user);
    setModalType(action);
    setIsModalOpen(true);
  };

  const confirmUserAction = () => {
    if (!selectedUser) return;

    let newStatus = selectedUser?.status;
    let suspensionReason = '';

    if (modalType === 'suspend') {
      newStatus = 'suspended';
      suspensionReason = 'Suspended by admin';
    } else if (modalType === 'activate') {
      newStatus = 'active';
      suspensionReason = '';
    }

    setUsers(prev => prev?.map(user => 
      user?.id === selectedUser?.id 
        ? { 
            ...user, 
            status: newStatus, 
            suspensionReason: suspensionReason || user?.suspensionReason,
            updatedAt: new Date()?.toISOString()
          } 
        : user
    ));

    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'suspended': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getUserTypeColor = (type) => {
    return type === 'seller' ? 'text-blue-600 bg-blue-50' : 'text-purple-600 bg-purple-50';
  };

  const getKycStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-orange-600 bg-orange-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const stats = {
    total: users?.length || 0,
    active: users?.filter(user => user?.status === 'active')?.length || 0,
    suspended: users?.filter(user => user?.status === 'suspended')?.length || 0,
    customers: users?.filter(user => user?.userType === 'customer')?.length || 0,
    sellers: users?.filter(user => user?.userType === 'seller')?.length || 0
  };

  return (
    <div className="flex-1 overflow-hidden bg-background">
      {/* Header Section */}
      <div className="bg-card border-b border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground">User Controls</h2>
            <p className="text-sm text-muted-foreground">Manage user accounts, suspension, and activation</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-foreground">{stats?.total}</div>
            <div className="text-sm text-muted-foreground">Total Users</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{stats?.active}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{stats?.suspended}</div>
            <div className="text-sm text-muted-foreground">Suspended</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">{stats?.customers}</div>
            <div className="text-sm text-muted-foreground">Customers</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{stats?.sellers}</div>
            <div className="text-sm text-muted-foreground">Sellers</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="customer">Customers</option>
            <option value="seller">Sellers</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          {filteredUsers?.map((user) => (
            <div key={user?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUserTypeColor(user?.userType)}`}>
                        {user?.userType?.charAt(0)?.toUpperCase() + user?.userType?.slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getKycStatusColor(user?.kycStatus)}`}>
                        KYC: {user?.kycStatus?.charAt(0)?.toUpperCase() + user?.kycStatus?.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(user?.status)}`}>
                    {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUserAction(user, 'details')}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    {user?.status === 'active' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUserAction(user, 'suspend')}
                        className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        <Icon name="UserX" size={16} />
                        Suspend
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUserAction(user, 'activate')}
                        className="text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                      >
                        <Icon name="UserCheck" size={16} />
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* User Stats Grid */}
              <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="ml-2 text-foreground">{user?.phone}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Location:</span>
                  <span className="ml-2 text-foreground">{user?.location}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Registered:</span>
                  <span className="ml-2 text-foreground">{user?.registrationDate}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Login:</span>
                  <span className="ml-2 text-foreground">{user?.lastLogin}</span>
                </div>
              </div>

              {/* Activity Stats */}
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Orders:</span>
                  <span className="ml-2 text-foreground font-medium">{user?.totalOrders}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {user?.userType === 'seller' ? 'Revenue:' : 'Spent:'}
                  </span>
                  <span className="ml-2 text-foreground font-medium">
                    ${(user?.totalRevenue || user?.totalSpent)?.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Suspension Reason */}
              {user?.status === 'suspended' && user?.suspensionReason && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm text-red-800">
                    <strong>Suspension Reason:</strong> {user?.suspensionReason}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredUsers?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {modalType === 'suspend' ? 'Suspend User Account' : 
               modalType === 'activate' ? 'Activate User Account' : 'User Details'}
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{selectedUser?.name}</div>
                  <div className="text-sm text-muted-foreground">{selectedUser?.email}</div>
                </div>
              </div>
            </div>

            {modalType === 'suspend' && (
              <p className="text-muted-foreground mb-4">
                Are you sure you want to suspend this user account? The user will not be able to access the platform until reactivated.
              </p>
            )}

            {modalType === 'activate' && (
              <p className="text-muted-foreground mb-4">
                Are you sure you want to activate this user account? The user will regain full access to the platform.
              </p>
            )}

            {modalType === 'details' && (
              <div className="space-y-3 mb-4">
                <div className="text-sm">
                  <span className="text-muted-foreground">User Type:</span>
                  <span className="ml-2 text-foreground">{selectedUser?.userType}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">KYC Status:</span>
                  <span className="ml-2 text-foreground">{selectedUser?.kycStatus}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Total Orders:</span>
                  <span className="ml-2 text-foreground">{selectedUser?.totalOrders}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Registration Date:</span>
                  <span className="ml-2 text-foreground">{selectedUser?.registrationDate}</span>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
              >
                {modalType === 'details' ? 'Close' : 'Cancel'}
              </Button>
              {modalType !== 'details' && (
                <Button
                  onClick={confirmUserAction}
                  className={`flex-1 ${modalType === 'suspend' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                >
                  {modalType === 'suspend' ? 'Suspend User' : 'Activate User'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserControls;